import React, { useEffect, useMemo, useRef, useState } from 'react';
import { isValidIban, normalizeIban } from '../utils/pe-papers/ibanValidation';
import { submitNeukundenIntake, uploadBwaFiles } from '../services/neukundenIntakeService';
import IntakeLegalModal, { IntakeLegalMode } from '../components/intake/IntakeLegalModal';
import type { IntakePackageType, IntakeRechtsform, NeukundenIntakeFormData } from '../types/intake';

const DRAFT_KEY = 'pe_neukunden_intake_draft_v1';

const RECHTSFORMEN: { id: IntakeRechtsform; label: string }[] = [
  { id: 'einzel', label: 'Einzelunternehmen' },
  { id: 'gmbh', label: 'GmbH' },
  { id: 'gbr', label: 'GbR' },
];

const PACKAGES: { id: IntakePackageType; title: string; price: string; defaultNetto: number }[] = [
  { id: 'insider', title: 'INSIDER', price: '167 € netto / Monat · 12 Monate', defaultNetto: 167 },
  { id: 'insider_solo', title: 'INSIDER Soloselbstständig', price: '94 € netto / Monat · 12 Monate', defaultNetto: 94 },
];

const emptyForm = (): NeukundenIntakeFormData => ({
  salonName: '',
  rechtsform: 'einzel',
  firmenbezeichnung: '',
  inhaber: '',
  kontoinhaber: '',
  kontoinhaberIdentisch: true,
  strasse: '',
  plz: '',
  ort: '',
  iban: '',
  ustId: '',
  email: '',
  emailConfirm: '',
  whatsapp: '',
  website: '',
  social: '',
  packageType: '',
  nettopreis: '',
  vertragAccepted: false,
  sepaAccepted: false,
  bwaLater: false,
  files: [],
});

function loadDraft(): NeukundenIntakeFormData {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return emptyForm();
    const parsed = JSON.parse(raw) as Partial<NeukundenIntakeFormData>;
    return { ...emptyForm(), ...parsed, files: [] };
  } catch {
    return emptyForm();
  }
}

function saveDraft(form: NeukundenIntakeFormData) {
  try {
    const { files: _files, ...rest } = form;
    localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
  } catch {
    /* ignore */
  }
}

function formatIbanBlocks(raw: string): string {
  const clean = normalizeIban(raw).replace(/[^A-Z0-9]/g, '').slice(0, 34);
  return clean.replace(/(.{4})/g, '$1 ').trim();
}

async function looksLikePdf(file: File): Promise<boolean> {
  const name = file.name.toLowerCase();
  const type = (file.type || '').toLowerCase();
  if (name.endsWith('.pdf') || type.includes('pdf')) return true;
  try {
    const head = new TextDecoder().decode(await file.slice(0, 5).arrayBuffer());
    return head.startsWith('%PDF');
  } catch {
    return false;
  }
}

const fieldClass =
  'w-full max-w-full rounded-lg bg-[#0F172A] border border-slate-600 px-3 py-3 text-base text-[#F8FAFC] placeholder:text-slate-500 focus:outline-none focus:border-[#DCB68A]';

const NeukundenIntakePage: React.FC = () => {
  const [form, setForm] = useState<NeukundenIntakeFormData>(() => loadDraft());
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState('');
  const [readingFiles, setReadingFiles] = useState(false);
  const [done, setDone] = useState(false);
  const [bwaOffen, setBwaOffen] = useState(false);
  const [bwaUploadWarn, setBwaUploadWarn] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [legalMode, setLegalMode] = useState<IntakeLegalMode | null>(null);
  const [draftHint, setDraftHint] = useState(() => {
    try {
      return Boolean(localStorage.getItem(DRAFT_KEY));
    } catch {
      return false;
    }
  });
  const filesRef = useRef<File[]>([]);

  const kontoinhaber = form.kontoinhaberIdentisch ? form.inhaber : form.kontoinhaber;
  const ibanOk = useMemo(() => isValidIban(form.iban), [form.iban]);

  useEffect(() => {
    saveDraft(form);
    setDraftHint(true);
  }, [form]);

  const set = <K extends keyof NeukundenIntakeFormData>(key: K, value: NeukundenIntakeFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const addFiles = (list: FileList | File[]) => {
    const incoming = Array.from(list);
    setReadingFiles(true);
    void (async () => {
      try {
        const checks = await Promise.all(incoming.map(async (file) => ({ file, ok: await looksLikePdf(file) })));
        const rejected = checks.filter((item) => !item.ok);
        const pdfs = checks.filter((item) => item.ok).map((item) => item.file);
        if (rejected.length) {
          setError('Die Datei ist keine PDF. Auf dem Handy die Datei mit Endung .pdf wählen.');
        } else {
          setError('');
        }
        setForm((prev) => {
          const next = [...prev.files, ...pdfs].slice(0, 5);
          if (prev.files.length + pdfs.length > 5) setError('Maximal 5 PDF-Dateien.');
          filesRef.current = next;
          return { ...prev, files: next, bwaLater: next.length ? false : prev.bwaLater };
        });
      } finally {
        setReadingFiles(false);
      }
    })();
  };

  const validate = (opts?: { allowWithoutBwa?: boolean }): string => {
    if (!form.salonName.trim()) return 'Salonname fehlt.';
    if (form.rechtsform !== 'einzel' && !form.firmenbezeichnung.trim()) {
      return form.rechtsform === 'gmbh' ? 'GmbH-Bezeichnung fehlt.' : 'GbR-Bezeichnung fehlt.';
    }
    if (!form.inhaber.trim()) return 'Name der Person fehlt.';
    if (!kontoinhaber.trim()) return 'Kontoinhaber fehlt.';
    if (!form.strasse.trim() || !form.plz.trim() || !form.ort.trim()) return 'Adresse unvollständig.';
    if (!ibanOk) return 'IBAN ist ungültig.';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return 'E-Mail ungültig.';
    if (form.email.trim().toLowerCase() !== form.emailConfirm.trim().toLowerCase()) return 'E-Mail-Wiederholung stimmt nicht überein.';
    if (!form.whatsapp.trim()) return 'WhatsApp-Nummer fehlt.';
    if (!form.packageType) return 'Bitte ein Paket wählen.';
    const netto = parseFloat(String(form.nettopreis || '').replace(',', '.').replace(/[^\d.-]/g, ''));
    if (!Number.isFinite(netto) || netto <= 0) return 'Bitte den monatlichen Nettopreis eintragen.';
    if (!form.vertragAccepted || !form.sepaAccepted) return 'Vertrag und SEPA-Mandat müssen bestätigt werden.';
    if (!opts?.allowWithoutBwa && !form.bwaLater && form.files.length === 0 && filesRef.current.length === 0) {
      return 'Bitte BWA-PDFs hochladen oder „später nachreichen“ wählen.';
    }
    return '';
  };

  const runSubmit = async (opts?: { skipBwaUpload?: boolean }) => {
    if (readingFiles) {
      setError('Die PDF wird noch gelesen. Einen Moment, dann erneut senden.');
      return;
    }
    const msg = validate({ allowWithoutBwa: opts?.skipBwaUpload || form.bwaLater });
    if (msg) {
      setError(msg);
      return;
    }
    setBusy(true);
    setError('');
    setBwaUploadWarn('');
    setProgress('Speichere Anmeldung…');
    const files = opts?.skipBwaUpload ? [] : (filesRef.current.length ? filesRef.current : form.files);
    try {
      let uploaded: Awaited<ReturnType<typeof uploadBwaFiles>> = [];
      let uploadFailedMsg = '';

      // 1) Optional BWA-Upload — scheitert er, speichern wir trotzdem die Anmeldung
      if (files.length) {
        setProgress(`Lade BWA 1/${files.length}…`);
        try {
          uploaded = await uploadBwaFiles(
            form.salonName,
            files,
            ({ index, total, fileName }) => {
              setProgress(`Lade BWA ${index}/${total}: ${fileName}`);
            },
            form.inhaber
          );
        } catch (upErr: any) {
          console.error('[neukunden-intake] BWA-Upload:', upErr);
          uploadFailedMsg = upErr?.message || 'BWA-Upload fehlgeschlagen.';
          uploaded = [];
        }
      }

      setProgress('Speichere Anmeldung…');
      await submitNeukundenIntake(
        {
          ...form,
          kontoinhaber,
          bwaLater: uploaded.length === 0,
          files: [],
        },
        uploaded
      );
      // Entwurf behalten — erneutes Testen ohne alles neu tippen
      setBwaOffen(uploaded.length === 0);
      setBwaUploadWarn(uploadFailedMsg);
      setDone(true);
    } catch (err: any) {
      console.error('[neukunden-intake]', err);
      setError(err?.message || 'Übermittlung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
      setProgress('');
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await runSubmit();
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-[#F8FAFC] flex items-center justify-center p-6">
        <div className="max-w-xl w-full bg-[#1E293B] border border-[#DCB68A]/40 rounded-2xl p-8 text-center">
          <p className="text-[#DCB68A] text-xs font-black uppercase tracking-widest mb-3">Eingegangen</p>
          <h1 className="text-2xl font-black mb-4">Vielen Dank!</h1>
          <p className="text-slate-300 leading-relaxed">
            {bwaOffen
              ? 'Deine Anmeldung ist eingegangen. Die BWA fehlt noch bzw. konnte nicht hochgeladen werden.'
              : 'Deine Daten und Unterlagen sind sicher bei uns eingegangen. Unser Team prüft Deine BWA und richtet Dein Kalkulationsprogramm ein.'}
          </p>
          {bwaUploadWarn && (
            <p className="mt-4 text-amber-300 text-sm text-left bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
              Hinweis zum Datei-Upload: {bwaUploadWarn}
            </p>
          )}
          {bwaOffen && (
            <a
              href="/bwa-upload"
              className="mt-6 inline-block px-6 py-4 rounded-xl bg-[#DCB68A] text-black font-black uppercase tracking-widest"
            >
              BWA nachträglich hochladen
            </a>
          )}
        </div>
      </div>
    );
  }

  return (
      <div className="min-h-screen overflow-x-hidden bg-[#0A0A0A] text-[#F8FAFC]">
      <div className="max-w-3xl mx-auto px-4 py-6 sm:py-10">
        <p className="text-[#DCB68A] text-xs font-black uppercase tracking-[0.25em] mb-2">PreisExpert</p>
        <h1 className="text-3xl font-black mb-2">Neukunden-Anmeldung</h1>
        <p className="text-slate-400 mb-4">Stammdaten, Paket und BWA — verbindlich einreichen.</p>
        {draftHint && (form.salonName || form.inhaber || form.email) && (
          <p className="mb-6 text-sm text-emerald-300/90 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
            Entwurf wiederhergestellt — du musst die Felder nicht nochmal tippen. PDFs ggf. erneut anhängen.
          </p>
        )}

        <form onSubmit={onSubmit} className="space-y-8">
          <section className="bg-[#1E293B] rounded-2xl p-4 sm:p-6 space-y-4 border border-slate-700">
            <h2 className="font-black uppercase tracking-widest text-sm text-[#DCB68A]">Stammdaten</h2>
            <label className="block text-xs uppercase tracking-wider text-slate-400">Salonname *
              <input className={`${fieldClass} mt-1`} value={form.salonName} onChange={(e) => set('salonName', e.target.value)} />
            </label>
            <div>
              <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">Rechtsform *</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {RECHTSFORMEN.map((rf) => {
                  const active = form.rechtsform === rf.id;
                  return (
                    <button
                      type="button"
                      key={rf.id}
                      onClick={() => setForm((prev) => ({
                        ...prev,
                        rechtsform: rf.id,
                        firmenbezeichnung: rf.id === 'einzel' ? '' : prev.firmenbezeichnung,
                      }))}
                      className={`rounded-lg px-3 py-3 text-sm font-black border-2 leading-tight ${active ? 'border-[#DCB68A] text-[#DCB68A]' : 'border-slate-600 text-slate-300'}`}
                    >
                      {rf.label}
                    </button>
                  );
                })}
              </div>
            </div>
            {form.rechtsform !== 'einzel' && (
              <label className="block text-xs uppercase tracking-wider text-slate-400">
                {form.rechtsform === 'gmbh' ? 'GmbH-Bezeichnung *' : 'GbR-Bezeichnung *'}
                <input className={`${fieldClass} mt-1`} value={form.firmenbezeichnung} onChange={(e) => set('firmenbezeichnung', e.target.value)} placeholder={form.rechtsform === 'gmbh' ? 'z. B. Haarstudio Luna GmbH' : 'z. B. Müller & Schmidt GbR'} />
                <span className="block normal-case tracking-normal text-slate-500 mt-1">Kann vom Salonnamen abweichen.</span>
              </label>
            )}
            <label className="block text-xs uppercase tracking-wider text-slate-400">
              {form.rechtsform === 'gmbh' ? 'Geschäftsführer *' : form.rechtsform === 'gbr' ? 'Gesellschafter *' : 'Unternehmer / Inhaber *'}
              <input className={`${fieldClass} mt-1`} value={form.inhaber} onChange={(e) => set('inhaber', e.target.value)} />
            </label>
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={form.kontoinhaberIdentisch} onChange={(e) => set('kontoinhaberIdentisch', e.target.checked)} />
              Kontoinhaber identisch mit {form.rechtsform === 'gmbh' ? 'Geschäftsführer' : form.rechtsform === 'gbr' ? 'Gesellschafter' : 'Unternehmer'}
            </label>
            {!form.kontoinhaberIdentisch && (
              <label className="block text-xs uppercase tracking-wider text-slate-400">Kontoinhaber *
                <input className={`${fieldClass} mt-1`} value={form.kontoinhaber} onChange={(e) => set('kontoinhaber', e.target.value)} />
              </label>
            )}
            <label className="block text-xs uppercase tracking-wider text-slate-400">Straße & Hausnummer *
              <input className={`${fieldClass} mt-1`} value={form.strasse} onChange={(e) => set('strasse', e.target.value)} />
            </label>
            <div className="grid grid-cols-1 min-[480px]:grid-cols-[7rem_1fr] gap-3">
              <label className="block text-xs uppercase tracking-wider text-slate-400">PLZ *
                <input className={`${fieldClass} mt-1`} value={form.plz} onChange={(e) => set('plz', e.target.value)} />
              </label>
              <label className="block text-xs uppercase tracking-wider text-slate-400">Ort *
                <input className={`${fieldClass} mt-1`} value={form.ort} onChange={(e) => set('ort', e.target.value)} />
              </label>
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-400">IBAN *
              <input
                className={`${fieldClass} mt-1 font-mono ${form.iban && !ibanOk ? 'border-red-500' : ''}`}
                value={form.iban}
                onChange={(e) => set('iban', formatIbanBlocks(e.target.value))}
                placeholder="DE89 3704 0044 0532 0130 00"
              />
            </label>
            <label className="block text-xs uppercase tracking-wider text-slate-400">USt-IdNr. (optional)
              <input className={`${fieldClass} mt-1`} value={form.ustId} onChange={(e) => set('ustId', e.target.value)} />
            </label>
            <label className="block text-xs uppercase tracking-wider text-slate-400">E-Mail *
              <input type="email" className={`${fieldClass} mt-1`} value={form.email} onChange={(e) => set('email', e.target.value)} />
            </label>
            <label className="block text-xs uppercase tracking-wider text-slate-400">E-Mail wiederholen *
              <input type="email" className={`${fieldClass} mt-1`} value={form.emailConfirm} onChange={(e) => set('emailConfirm', e.target.value)} />
            </label>
            <label className="block text-xs uppercase tracking-wider text-slate-400">WhatsApp-Mobilnummer *
              <input className={`${fieldClass} mt-1`} value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} />
            </label>
            <label className="block text-xs uppercase tracking-wider text-slate-400">Website (optional)
              <input className={`${fieldClass} mt-1`} value={form.website} onChange={(e) => set('website', e.target.value)} />
            </label>
            <label className="block text-xs uppercase tracking-wider text-slate-400">Social Media (optional)
              <input className={`${fieldClass} mt-1`} value={form.social} onChange={(e) => set('social', e.target.value)} />
            </label>
          </section>

          <section className="space-y-3">
            <h2 className="font-black uppercase tracking-widest text-sm text-[#DCB68A]">Paket</h2>
            <div className="grid gap-3">
              {PACKAGES.map((pkg) => {
                const active = form.packageType === pkg.id;
                return (
                  <button
                    type="button"
                    key={pkg.id}
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        packageType: pkg.id,
                        // Prefill nur, wenn noch leer oder noch der andere Paket-Standard
                        nettopreis:
                          !prev.nettopreis ||
                          prev.nettopreis === '167' ||
                          prev.nettopreis === '94' ||
                          prev.nettopreis === '49'
                            ? String(pkg.defaultNetto)
                            : prev.nettopreis,
                      }));
                    }}
                    className={`text-left rounded-xl p-4 bg-[#1E293B] border-2 ${active ? 'border-[#DCB68A]' : 'border-slate-700'}`}
                  >
                    <div className="font-black">{pkg.title}</div>
                    <div className="text-sm text-slate-300">{pkg.price}</div>
                  </button>
                );
              })}
            </div>
            <label className="block text-xs uppercase tracking-wider text-slate-400">
              Monatlicher Nettopreis (EUR) *
              <input
                className={`${fieldClass} mt-1`}
                inputMode="decimal"
                value={form.nettopreis}
                onChange={(e) => set('nettopreis', e.target.value.replace(/[^\d,.]/g, ''))}
                placeholder="z. B. 167"
              />
              <span className="mt-1 block text-[11px] normal-case tracking-normal text-slate-500">
                Der Preis, der für dich gilt (netto / Monat). Bei Paketwahl wird ein Vorschlag eingetragen — du kannst ihn anpassen.
              </span>
            </label>
          </section>

          <section className="bg-[#1E293B] rounded-2xl p-4 sm:p-6 space-y-4 border border-slate-700">
            <h2 className="font-black uppercase tracking-widest text-sm text-[#DCB68A]">Vertrag & SEPA</h2>
            <p className="text-sm text-slate-300 flex flex-col sm:flex-row gap-3">
              <button type="button" className="text-[#DCB68A] underline text-left" onClick={() => setLegalMode('vertrag')}>
                Muster-Nutzungsvertrag und SEPA-Bedingungen einsehen
              </button>
              <button type="button" className="text-[#DCB68A] underline text-left" onClick={() => setLegalMode('datenschutz')}>
                Datenschutzerklärung einsehen
              </button>
            </p>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={form.vertragAccepted} onChange={(e) => set('vertragAccepted', e.target.checked)} />
              Ich bestätige den Kalkulationsvertrag verbindlich.
            </label>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={form.sepaAccepted} onChange={(e) => set('sepaAccepted', e.target.checked)} />
              Ich erteile das SEPA-Lastschriftmandat. Der Einzug erfolgt erst nach Bereitstellung meines Programms.
            </label>
            <p className="text-xs text-slate-400">
              DSGVO: Die Verarbeitung erfolgt zur Vertragserfüllung gemäß Art. 6 Abs. 1 lit. b DSGVO.
            </p>
          </section>

          <section className="bg-[#1E293B] rounded-2xl p-4 sm:p-6 space-y-4 border border-red-500/60">
            <div className="border border-[#DCB68A] rounded-xl p-4 text-sm leading-relaxed">
              <p className="font-black text-[#DCB68A] uppercase tracking-wider mb-2">Wichtig zur BWA-Einreichung</p>
              Deine BWA muss zwingend im PDF-Format übermittelt werden. Fordere bei Deinem Steuerberater eine BWA inklusive Wertenachweis und Liquiditätsauswertung an!
              (Achtung: Bitte keine reine SuSa / Summen- und Saldenliste hochladen. Der Wertenachweis schlüsselt die genauen Kostenpositionen auf).
            </div>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => { e.preventDefault(); setDragOver(false); if (e.dataTransfer.files) addFiles(e.dataTransfer.files); }}
              className={`rounded-xl border-2 border-dashed p-4 sm:p-6 text-center overflow-hidden ${dragOver ? 'border-[#DCB68A] bg-[#0F172A]' : 'border-slate-600'}`}
            >
              <p className="mb-3 text-sm">PDF hierher ziehen oder auswählen (max. 5).</p>
              <input
                className="block w-full max-w-full text-sm file:mr-3 file:mb-2 file:rounded-lg file:border-0 file:bg-[#DCB68A] file:px-3 file:py-2 file:font-bold file:text-black"
                type="file"
                accept=".pdf,application/pdf,application/x-pdf"
                multiple
                onChange={(e) => { if (e.target.files) addFiles(e.target.files); e.target.value = ''; }}
              />
            </div>
            <ul className="space-y-2">
              {form.files.map((file, idx) => (
                <li key={`${file.name}-${idx}`} className="flex items-center justify-between gap-3 bg-[#0F172A] rounded-lg px-3 py-2 text-sm">
                  <span className="min-w-0 break-all">{file.name} · {(file.size / (1024 * 1024)).toFixed(2)} MB</span>
                  <button type="button" className="text-red-400" onClick={() => {
                    const next = form.files.filter((_, i) => i !== idx);
                    filesRef.current = next;
                    setForm((prev) => ({ ...prev, files: next, bwaLater: next.length ? false : prev.bwaLater }));
                  }}>Entfernen</button>
                </li>
              ))}
            </ul>
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                className="mt-1"
                checked={form.bwaLater && form.files.length === 0}
                onChange={(e) => setForm((prev) => ({
                  ...prev,
                  bwaLater: e.target.checked && prev.files.length === 0,
                }))}
              />
              <span>Ich habe die BWA noch nicht. Ich lade sie später hoch.</span>
            </label>
            {form.bwaLater && form.files.length === 0 && (
              <a
                href="/bwa-upload"
                target="_blank"
                rel="noreferrer"
                className="inline-block px-4 py-3 rounded-xl bg-[#DCB68A] text-black text-sm font-black uppercase tracking-wider"
              >
                BWA nachträglich hochladen
              </a>
            )}
          </section>

          {error && <p className="text-red-400 text-sm font-bold">{error}</p>}
          {busy && progress && (
            <p className="text-[#DCB68A] text-sm font-bold animate-pulse">{progress}</p>
          )}

          <button
            type="submit"
            disabled={busy || readingFiles}
            className="w-full py-4 px-4 rounded-xl bg-[#DCB68A] text-black text-sm sm:text-base font-black uppercase tracking-wide leading-snug disabled:opacity-60"
          >
            {busy || readingFiles
              ? (progress || 'Wird übermittelt…')
              : 'Anmeldung & BWA jetzt verbindlich einreichen ❯'}
          </button>
          <button
            type="button"
            disabled={busy || readingFiles}
            onClick={() => void runSubmit({ skipBwaUpload: true })}
            className="w-full py-3 px-4 rounded-xl border border-slate-600 text-slate-200 text-xs sm:text-sm font-bold uppercase tracking-wide disabled:opacity-60"
          >
            Nur Anmeldung senden (BWA später / ohne Datei-Upload)
          </button>
          <p className="text-[11px] text-slate-500 text-center">
            Wenn der PDF-Upload hängt: unteren Button nutzen — Stammdaten landen trotzdem im Hub.
          </p>
        </form>
        <IntakeLegalModal
          mode={legalMode}
          packageType={form.packageType}
          onClose={() => setLegalMode(null)}
        />
      </div>
    </div>
  );
};

export default NeukundenIntakePage;
